/**
 * newsletter-confirm.js — 200bar
 *
 * Rend visible la confirmation d'inscription à la newsletter.
 *
 * Problème corrigé : les formulaires `{% form 'customer' %}` rechargent la page
 * et affichent leur confirmation tout en bas (footer, ~17 000 px). La page
 * repartant en haut, l'internaute ne voyait jamais le message et pouvait croire
 * que son inscription avait échoué.
 *
 * Principe : on ne touche PAS à l'envoi du formulaire — c'est Shopify qui
 * enregistre l'inscription, et ça fonctionne. On se contente de lire SA
 * confirmation au chargement de la page, puis de l'afficher en haut de l'écran,
 * là où l'internaute se trouve après le rechargement.
 *
 * Aucune interception réseau : pas de faux négatif possible, et la protection
 * anti-spam hCaptcha de Shopify suit son cours normalement. Si ce script ne se
 * charge pas, le message d'origine de Shopify reste affiché dans le footer.
 *
 * Déjà inscrit : Shopify renvoie la même réponse qu'il s'agisse d'une nouvelle
 * adresse ou d'une adresse connue — le distinguer côté serveur permettrait de
 * tester si n'importe quelle adresse est cliente. On mémorise donc localement
 * (empreinte, jamais l'adresse en clair) les adresses envoyées depuis cet
 * appareil, pour ne pas promettre un code –10 % qui ne partira pas. Limite
 * assumée : un abonné qui s'inscrit depuis un nouvel appareil verra le message
 * de bienvenue.
 *
 * Couvre les deux points d'inscription :
 *   - section « Rejoignez le club »  → blocks/email-signup.liquid
 *   - footer « Rejoindre le club »   → sections/footer-200bar.liquid
 */
(function () {
  'use strict';

  if (window.__nl200barConfirmLoaded) return;
  window.__nl200barConfirmLoaded = true;

  /* Deux messages : première inscription (le Flow envoie le code) et ré-envoi
     d'une adresse déjà soumise depuis cet appareil (aucun code ne partira). */
  var MSG = {
    nouveau: {
      title: 'Bienvenue à bord.',
      text: 'Votre inscription est confirmée. Votre code –10 % arrive par e-mail.'
    },
    connu: {
      title: 'Vous êtes déjà inscrit.',
      text: 'Cette adresse est déjà enregistrée — vous n’avez rien de plus à faire.'
    }
  };
  var CLOSE_LABEL = 'Fermer';
  var AUTO_HIDE_MS = 12000;

  /* Marqueurs de succès rendus par Shopify (form.posted_successfully?). */
  var SUCCESS_SELECTOR = '.footer-200bar__form-msg--ok, [id^="Email-signup__message-success"]';

  var CSS = [
    /* Bandeau fixe : l'internaute revient en haut de page après l'envoi. */
    '.nl200-banner{position:fixed;z-index:2147483000;top:16px;left:50%;',
    'transform:translateX(-50%);display:flex;align-items:flex-start;gap:12px;',
    'width:calc(100% - 32px);max-width:460px;padding:16px 18px;',
    'background:#024444;color:#fff;border-radius:3px;',
    'box-shadow:0 8px 28px rgba(0,0,0,.28);',
    'font-family:"Barlow",system-ui,sans-serif;',
    /* Pas de fill-mode : si l'animation ne démarre pas, le bandeau reste visible. */
    'animation:nl200Drop 380ms cubic-bezier(.2,.8,.3,1)}',
    '.nl200-banner__icon{flex:0 0 auto;width:22px;height:22px;margin-top:1px}',
    '.nl200-banner__body{flex:1 1 auto;min-width:0}',
    '.nl200-banner__title{margin:0 0 3px;font-size:16px;font-weight:600;line-height:1.3}',
    '.nl200-banner__text{margin:0;font-size:13.5px;line-height:1.5;color:rgba(255,255,255,.88)}',
    '.nl200-banner__close{flex:0 0 auto;margin:-4px -6px 0 0;padding:4px;',
    'background:none;border:none;color:rgba(255,255,255,.7);cursor:pointer;',
    'font-size:18px;line-height:1;border-radius:2px}',
    '.nl200-banner__close:hover{color:#fff}',
    '.nl200-banner__close:focus-visible{outline:2px solid #fff;outline-offset:1px}',

    /* Confirmation en place, à l'endroit du formulaire. */
    '.nl200-confirm{display:flex;align-items:flex-start;gap:10px;',
    'padding:14px 16px;border-radius:3px;',
    'background:rgba(255,255,255,.09);border:1px solid rgba(255,255,255,.22)}',
    '.nl200-confirm--light{background:rgba(3,89,89,.07);border-color:rgba(3,89,89,.28)}',
    '.nl200-confirm__icon{flex:0 0 auto;width:20px;height:20px;margin-top:1px}',
    '.nl200-confirm__title{margin:0 0 3px;font-size:15px;font-weight:600;line-height:1.3}',
    '.nl200-confirm__text{margin:0;font-size:13px;line-height:1.5;opacity:.85}',

    /* On n'anime que le déplacement, jamais l'opacité : une animation qui ne
       progresse pas (onglet en arrière-plan, moteur bridé) laisserait sinon le
       bandeau invisible. Au pire il s'affiche 14 px plus haut. */
    '@keyframes nl200Drop{from{transform:translate(-50%,-14px)}',
    'to{transform:translate(-50%,0)}}',
    '@media (prefers-reduced-motion:reduce){.nl200-banner{animation:none}}'
  ].join('');

  function injectStyles() {
    if (document.getElementById('nl200-confirm-styles')) return;
    var s = document.createElement('style');
    s.id = 'nl200-confirm-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function checkIcon(cls) {
    return '<svg class="' + cls + '" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
      ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="10"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>';
  }

  /* ---- Mémoire locale des adresses déjà envoyées depuis cet appareil ----
     Shopify répond exactement la même chose que l'adresse soit nouvelle ou
     déjà inscrite (le distinguer côté serveur permettrait de tester si une
     adresse est cliente). On se rabat donc sur une mémoire navigateur.
     On ne stocke qu'une empreinte, jamais l'adresse en clair. */

  var STORE_KEY = 'nl200:sent';
  var REPEAT_KEY = 'nl200:repeat';

  function fingerprint(email) {
    var s = String(email).trim().toLowerCase();
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(36);
  }

  function readSent() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  /* Au moment de l'envoi (sans l'intercepter), on note l'adresse et on retient
     s'il s'agit d'un ré-envoi. Le drapeau est relu après le rechargement. */
  function rememberOnSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var type = form.querySelector('input[name="form_type"]');
    if (!type || type.value !== 'customer') return;
    var field = form.querySelector('input[name="contact[email]"]');
    if (!field || !field.value) return;

    try {
      var fp = fingerprint(field.value);
      var sent = readSent();
      sessionStorage.setItem(REPEAT_KEY, sent.indexOf(fp) !== -1 ? '1' : '0');
      if (sent.indexOf(fp) === -1) {
        sent.push(fp);
        localStorage.setItem(STORE_KEY, JSON.stringify(sent.slice(-30)));
      }
    } catch (e) {
      /* Stockage indisponible (navigation privée) : message par défaut. */
    }
  }

  function pickMessage() {
    try {
      var flag = sessionStorage.getItem(REPEAT_KEY);
      sessionStorage.removeItem(REPEAT_KEY);
      return flag === '1' ? MSG.connu : MSG.nouveau;
    } catch (e) {
      return MSG.nouveau;
    }
  }

  function showBanner(msg) {
    var b = document.createElement('div');
    b.className = 'nl200-banner';
    b.setAttribute('role', 'status');
    b.setAttribute('aria-live', 'polite');
    b.innerHTML =
      checkIcon('nl200-banner__icon') +
      '<div class="nl200-banner__body">' +
      '<p class="nl200-banner__title"></p><p class="nl200-banner__text"></p></div>' +
      '<button type="button" class="nl200-banner__close" aria-label="' + CLOSE_LABEL + '">&times;</button>';
    b.querySelector('.nl200-banner__title').textContent = msg.title;
    b.querySelector('.nl200-banner__text').textContent = msg.text;

    var remove = function () { if (b.parentNode) b.remove(); };
    b.querySelector('.nl200-banner__close').addEventListener('click', remove);
    setTimeout(remove, AUTO_HIDE_MS);

    document.body.appendChild(b);
  }

  /* Remplace le champ e-mail par la confirmation, dans chaque formulaire. */
  function replaceForms(msg) {
    var forms = document.querySelectorAll('form');
    Array.prototype.forEach.call(forms, function (form) {
      var type = form.querySelector('input[name="form_type"]');
      if (!type || type.value !== 'customer') return;
      if (!form.querySelector('input[name="contact[email]"]')) return;

      var row = form.querySelector('.footer-200bar__email-row, .email-signup__input-group');
      if (!row) return;

      var dark = !!form.closest('.footer-200bar');
      var box = document.createElement('div');
      box.className = 'nl200-confirm' + (dark ? '' : ' nl200-confirm--light');
      box.innerHTML =
        checkIcon('nl200-confirm__icon') +
        '<div><p class="nl200-confirm__title"></p><p class="nl200-confirm__text"></p></div>';
      box.querySelector('.nl200-confirm__title').textContent = msg.title;
      box.querySelector('.nl200-confirm__text').textContent = msg.text;

      row.replaceWith(box);

      /* Le message d'origine de Shopify ferait doublon. */
      form.querySelectorAll(SUCCESS_SELECTOR).forEach(function (n) { n.remove(); });
    });
  }

  /* Évite de ré-afficher la confirmation si l'internaute recharge la page. */
  function cleanUrl() {
    if (!window.history || !history.replaceState) return;
    if (location.search.indexOf('customer_posted') === -1) return;
    var url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function run() {
    /* On écoute l'envoi sans jamais l'intercepter : le formulaire part
       normalement, hCaptcha compris. */
    document.addEventListener('submit', rememberOnSubmit, true);

    /* Source de vérité : la confirmation rendue par Shopify lui-même. */
    var posted = !!document.querySelector(SUCCESS_SELECTOR) ||
      /[?&]customer_posted=true/.test(location.search);
    if (!posted) return;

    var msg = pickMessage();
    injectStyles();
    replaceForms(msg);
    showBanner(msg);
    cleanUrl();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
